import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  FileText, Calculator, Send, Users, RefreshCw, Mail,
  ArrowRight, Clock, DollarSign
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  FileText: FileText,
  Calculator: Calculator,
  Send: Send,
  Users: Users,
  RefreshCw: RefreshCw,
  Mail: Mail,
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const processSteps = [
    { step: "01", title: "Schedule", desc: "Book your appointment online or call us" },
    { step: "02", title: "Gather", desc: "Bring your tax documents to your appointment" },
    { step: "03", title: "Prepare", desc: "We prepare your return with expert care" },
    { step: "04", title: "File & Relax", desc: "E-file and track your refund status" },
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Our Services</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 font-serif">
              Comprehensive Tax Solutions
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mt-6">
              From simple returns to complex tax situations, we provide personalized services 
              tailored to your unique financial needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-[#111111] border border-white/5 p-8 animate-pulse h-64" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || FileText;
                return (
                  <div 
                    key={service.id}
                    className="service-card flex flex-col h-full opacity-0 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`service-item-${service.id}`}
                  >
                    <IconComponent className="w-12 h-12 text-[#D4AF37] mb-6" strokeWidth={1.5} />
                    <h3 className="text-2xl font-semibold mb-4 font-serif">{service.name}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6 flex-grow">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-6 border-t border-white/5">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <DollarSign size={14} className="text-[#D4AF37]" />
                          {service.price}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} className="text-[#D4AF37]" />
                          {service.duration} min
                        </span>
                      </div>
                    </div>
                    <a href="tel:+12027042459" className="mt-6">
                      <Button 
                        variant="outline"
                        className="w-full border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] rounded-sm"
                        data-testid={`call-service-${service.id}-btn`}
                      >
                        Call to Inquire
                        <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-[#D4AF37]">How It Works</span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 font-serif">
              Our Simple Process
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={step.step}
                className="relative opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-6xl font-bold text-[#D4AF37]/20 font-serif mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold mb-2 font-serif">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.desc}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-full h-px bg-[#D4AF37]/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-16 bg-[#0A0A0A] border-y border-white/5" data-testid="pricing-note">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h3 className="text-2xl font-semibold mb-4 font-serif">Transparent Pricing</h3>
          <p className="text-muted-foreground leading-relaxed">
            Our prices are competitive and transparent. The final cost depends on the complexity 
            of your tax situation. We'll provide a clear estimate before beginning any work, 
            so there are no surprises.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-serif">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-muted-foreground mt-6">
            Call us today or just walk in - no appointment needed!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <a href="tel:+12027042459">
              <Button 
                className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold px-8 py-6 text-lg rounded-sm"
                data-testid="services-cta-call-btn"
              >
                Call Now
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-8 py-6 text-lg rounded-sm"
                data-testid="services-cta-contact-btn"
              >
                Have Questions?
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
